import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { TIER_NAMES } from '../config/subscriptions'
import { goToCustomerPortal } from '../config/stripeUrls'
import { getUserProfile, updateUserProfile } from '../services/userDataService'
import { supabase } from '../supabase/supabaseClient'

// SVG Icons
const Icons = {
  user: (color, size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/>
      <path d="M4 21c0-4 4-7 8-7s8 3 8 7"/>
    </svg>
  ),
  card: (color, size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
      <line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  rocket: (color, size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
    </svg>
  ),
  discord: (color, size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12h.01M15 12h.01M7.5 7.5c3.5-1 5.5-1 9 0M7 16.5c3.5 1 6.5 1 10 0M15.5 17c0 1 1.5 3 2 3 1.5 0 2.833-1.667 3.5-3 .667-1.667.5-5.833-1-11.5-1.457-.96-3-1.5-4.5-1.5l-1 2h-5l-1-2c-1.5 0-3.043.54-4.5 1.5-1.5 5.667-1.667 9.833-1 11.5.667 1.333 2 3 3.5 3 .5 0 2-2 2-3"/>
    </svg>
  ),
  settings: (color, size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  logout: (color) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  )
}

const css = `
.pf-glass {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.018));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 24px 60px -36px rgba(0, 0, 0, 0.65), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
@keyframes pfFadeUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.pf-fade { animation: pfFadeUp 0.45s ease both; }
.pf-d1 { animation-delay: 0s; }
.pf-d2 { animation-delay: 0.07s; }
.pf-d3 { animation-delay: 0.15s; }
.pf-d4 { animation-delay: 0.23s; }
@media (prefers-reduced-motion: reduce) {
  .pf-fade { animation: none; }
}
.pf-btn {
  border: none;
  border-radius: 10px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, filter 0.15s ease, box-shadow 0.15s ease;
}
.pf-btn:hover { filter: brightness(1.12); transform: translateY(-1px); }
.pf-btn:active { transform: translateY(0); }
.pf-btn-blue {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  box-shadow: 0 8px 24px -10px rgba(59, 130, 246, 0.6);
}
.pf-btn-green {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 8px 24px -10px rgba(34, 197, 94, 0.5);
}
.pf-btn-discord {
  background: #5865F2;
  box-shadow: 0 8px 24px -10px rgba(88, 101, 242, 0.55);
}
.pf-btn-ghost {
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.09);
  color: #cbd5e1;
}
.pf-btn-ghost:hover { filter: none; transform: translateY(-1px); background: rgba(255, 255, 255, 0.06); }
.pf-hover-card {
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}
.pf-hover-card:hover {
  transform: translateY(-2px);
  border-color: rgba(59, 130, 246, 0.35);
  background: linear-gradient(180deg, rgba(59, 130, 246, 0.06), rgba(255, 255, 255, 0.02));
}
.pf-grad-text {
  background: linear-gradient(120deg, #60a5fa, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}
`

const iconTile = {
  width: '34px',
  height: '34px',
  borderRadius: '10px',
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}

const cardHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '18px'
}

const cardTitle = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#f1f5f9',
  letterSpacing: '-0.2px'
}

const rowBorder = '1px solid rgba(255, 255, 255, 0.055)'

const styles = {
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: rowBorder
  },
  infoRowLast: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0'
  },
  label: {
    fontSize: '13px',
    color: '#64748b'
  },
  value: {
    fontSize: '13.5px',
    color: '#e2e8f0',
    fontWeight: '500'
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.055)',
    margin: '16px 0'
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%'
  },
  innerPanel: {
    background: 'rgba(0, 0, 0, 0.28)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px'
  }
}

// Mobile layout — matches the app's profile screen (bookiecharity-mobile-app profile.tsx)
const ODDS_OPTIONS = [
  { value: 'american', short: 'American', example: '+150' },
  { value: 'decimal', short: 'Decimal', example: '2.50' },
  { value: 'fractional', short: 'Fractional', example: '3/2' }
]

const getTierColor = (tier) => {
  switch (tier) {
    case 'sports': return '#60a5fa'
    case 'horse_racing': return '#a78bfa'
    case 'full_access': return '#34d399'
    default: return '#94a3b8'
  }
}

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Get status display info
const getStatusInfo = (status) => {
  switch (status) {
    case 'active':
      return { label: 'Active', color: '#22c55e' }
    case 'trialing':
      return { label: 'Trial', color: '#60a5fa' }
    case 'past_due':
      return { label: 'Past Due', color: '#f59e0b' }
    case 'canceled':
      return { label: 'Canceled', color: '#ef4444' }
    case 'incomplete':
      return { label: 'Incomplete', color: '#f59e0b' }
    default:
      return { label: 'Active', color: '#22c55e' }
  }
}

const TierPill = ({ tier }) => {
  const tc = getTierColor(tier)
  return (
    <span style={{
      padding: '4px 12px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: '600',
      color: tc,
      border: `1px solid ${tc}55`,
      backgroundColor: `${tc}14`
    }}>
      {TIER_NAMES[tier] || 'Free'}
    </span>
  )
}

export default function ProfilePage() {
  const { user, subscriptionTier, signOut, oddsFormat, setOddsFormat } = useAuth()
  const [hoveredButton, setHoveredButton] = useState(null)
  const [profileData, setProfileData] = useState(null)
  const [discordData, setDiscordData] = useState(null)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640)

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  // Fetch profile data and discord status on mount
  useEffect(() => {
    if (user?.id) {
      getUserProfile(user.id).then(({ data }) => {
        if (data) setProfileData(data)
      })
    }
    if (user?.email) {
      supabase
        .from('discord_integrations')
        .select('discord_username, linked_at')
        .eq('email', user.email)
        .single()
        .then(({ data }) => {
          if (data) setDiscordData(data)
        })
    }
  }, [user])

  const handleNavigateToPricing = () => {
    window.location.hash = '#/pricing'
  }

  const handleLinkDiscord = () => {
    window.open('https://discord.gg/BV4dVEnx73', '_blank', 'noopener,noreferrer')
  }

  const changeOdds = (newFormat) => {
    if (newFormat === oddsFormat) return
    setOddsFormat(newFormat)
    if (user?.id) {
      updateUserProfile(user.id, { odds_format: newFormat })
    }
  }

  const oddsButtons = (fill = false) => (
    <div style={{ display: 'flex', gap: '10px', flex: fill ? 1 : undefined }}>
      {ODDS_OPTIONS.map((o) => {
        const sel = o.value === oddsFormat
        return (
          <button
            key={o.value}
            onClick={() => changeOdds(o.value)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '11px 8px',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              border: sel ? '1px solid rgba(59, 130, 246, 0.55)' : '1px solid rgba(255, 255, 255, 0.08)',
              background: sel ? 'rgba(59, 130, 246, 0.12)' : 'rgba(0, 0, 0, 0.28)'
            }}
          >
            <span style={{ fontSize: '13px', fontWeight: '600', color: sel ? '#60a5fa' : '#cbd5e1' }}>{o.short}</span>
            <span style={{ fontSize: '12px', fontWeight: '500', marginTop: '4px', fontVariantNumeric: 'tabular-nums', color: sel ? '#93c5fd' : '#64748b' }}>
              {o.example}
            </span>
          </button>
        )
      })}
    </div>
  )

  const discordBanner = (
    <div style={{ ...styles.innerPanel, display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px' }}>
      <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#5865F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {Icons.discord('#fff', 20)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {discordData ? discordData.discord_username : 'Not linked'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
          <div style={{ ...styles.statusDot, backgroundColor: discordData ? '#22c55e' : '#ef4444' }} />
          <span style={{ fontSize: '12px', fontWeight: '500', color: discordData ? '#22c55e' : '#ef4444' }}>
            {discordData ? 'Linked' : 'Not connected'}
          </span>
        </div>
      </div>
      {!isMobile && !discordData && (
        <button
          className="pf-btn pf-btn-discord"
          onClick={handleLinkDiscord}
          style={{ padding: '9px 16px', fontSize: '13px', flexShrink: 0 }}
        >
          Link Discord
        </button>
      )}
    </div>
  )

  const affiliateCard = (
    <div
      className="pf-glass pf-hover-card"
      onClick={() => { window.location.hash = '#/affiliates' }}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', cursor: 'pointer', padding: isMobile ? '20px' : '20px 24px' }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#f1f5f9', marginBottom: '5px', letterSpacing: '-0.2px' }}>
          Earn <span className="pf-grad-text">30% recurring</span> commission
        </div>
        <div style={{ fontSize: '12.5px', color: '#94a3b8', lineHeight: 1.55 }}>
          Refer Bookie Charity and earn 30% of every payment, for the lifetime of the subscription.
        </div>
      </div>
      <span style={{ color: '#60a5fa', fontSize: '18px', flexShrink: 0 }}>→</span>
    </div>
  )

  if (isMobile) {
    const isFree = subscriptionTier === 'free'
    const st = getStatusInfo(profileData?.subscription_status)

    return (
      <div style={{ backgroundColor: '#010714', minHeight: '100vh', padding: '16px', position: 'relative' }}>
        <style>{css}</style>
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Hero */}
          <div className="pf-fade pf-d1" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '12px', paddingBottom: '4px' }}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#f1f5f9', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email}
            </div>
            <div style={{ marginTop: '10px' }}>
              <TierPill tier={subscriptionTier} />
            </div>
          </div>

          {/* Subscription */}
          <div className="pf-glass pf-fade pf-d2" style={{ padding: '20px' }}>
            <div style={cardHeader}>
              <div style={iconTile}>{Icons.card('#8b9cb8', 16)}</div>
              <span style={cardTitle}>Subscription</span>
            </div>

            <div style={isFree ? styles.infoRowLast : styles.infoRow}>
              <span style={styles.label}>Member since</span>
              <span style={styles.value}>{formatDate(profileData?.created_at)}</span>
            </div>

            {!isFree && (
              <>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Status</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ ...styles.statusDot, backgroundColor: st.color }} />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: st.color }}>{st.label}</span>
                  </div>
                </div>
                <div style={styles.infoRowLast}>
                  <span style={styles.label}>Next billing</span>
                  <span style={styles.value}>{formatDate(profileData?.subscription_ends_at)}</span>
                </div>
              </>
            )}

            <div style={styles.divider} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {!isFree ? (
                <button className="pf-btn pf-btn-green" onClick={() => goToCustomerPortal()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '12px 0', borderRadius: '12px', fontSize: '14px' }}>
                  {Icons.card('#fff', 16)}
                  Manage Subscription
                </button>
              ) : (
                <button className="pf-btn pf-btn-blue" onClick={handleNavigateToPricing} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '12px 0', borderRadius: '12px', fontSize: '14px' }}>
                  {Icons.rocket('#fff', 16)}
                  Upgrade Plan
                </button>
              )}
              <button className="pf-btn pf-btn-ghost" onClick={handleNavigateToPricing} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '12px 0', borderRadius: '12px', fontSize: '14px' }}>
                View Pricing
              </button>
            </div>
          </div>

          {/* Discord */}
          <div className="pf-glass pf-fade pf-d3" style={{ padding: '20px' }}>
            <div style={cardHeader}>
              <div style={iconTile}>{Icons.discord('#8b9cb8', 16)}</div>
              <span style={cardTitle}>Discord</span>
            </div>

            {discordBanner}

            {!discordData && (
              <button className="pf-btn pf-btn-discord" onClick={handleLinkDiscord} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '12px 0', borderRadius: '12px', fontSize: '14px', marginTop: '12px' }}>
                {Icons.discord('#fff', 16)}
                Link Discord
              </button>
            )}
          </div>

          {/* Preferences */}
          <div className="pf-glass pf-fade pf-d3" style={{ padding: '20px' }}>
            <div style={cardHeader}>
              <div style={iconTile}>{Icons.settings('#8b9cb8', 16)}</div>
              <span style={cardTitle}>Preferences</span>
            </div>

            <div style={{ ...styles.label, marginBottom: '12px' }}>Odds Format</div>
            {oddsButtons()}
          </div>

          {/* Affiliate program */}
          <div className="pf-fade pf-d4">
            {affiliateCard}
          </div>

          {/* Sign out */}
          <button
            className="pf-btn pf-btn-ghost pf-fade pf-d4"
            onClick={() => signOut()}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '12px 0', borderRadius: '12px', fontSize: '14px', color: '#94a3b8', marginTop: '8px' }}
          >
            {Icons.logout('#94a3b8')}
            Sign Out
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ backgroundColor: '#010714', minHeight: '100vh', padding: '48px 24px 80px', position: 'relative' }}>
      <style>{css}</style>
      <div style={{ maxWidth: '960px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div className="pf-fade pf-d1" style={{ marginBottom: '28px' }}>
          <div style={{ color: '#3b82f6', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.4px', marginBottom: '10px' }}>
            Account
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, letterSpacing: '-0.6px', color: '#f1f5f9', margin: 0 }}>
            Profile &amp; Settings
          </h1>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {/* Account Card */}
          <div className="pf-glass pf-fade pf-d2" style={{ padding: '24px' }}>
            <div style={cardHeader}>
              <div style={iconTile}>{Icons.user('#8b9cb8', 17)}</div>
              <span style={cardTitle}>Account</span>
            </div>

            <div style={styles.infoRow}>
              <span style={styles.label}>Email</span>
              <span style={styles.value}>{user?.email}</span>
            </div>

            <div style={styles.infoRow}>
              <span style={styles.label}>Member since</span>
              <span style={styles.value}>{formatDate(profileData?.created_at)}</span>
            </div>

            <div style={styles.infoRow}>
              <span style={styles.label}>Plan</span>
              <TierPill tier={subscriptionTier} />
            </div>

            {subscriptionTier !== 'free' && (
              <>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Status</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ ...styles.statusDot, backgroundColor: getStatusInfo(profileData?.subscription_status).color }} />
                    <span style={{ color: getStatusInfo(profileData?.subscription_status).color, fontSize: '14px', fontWeight: '500' }}>
                      {getStatusInfo(profileData?.subscription_status).label}
                    </span>
                  </div>
                </div>

                <div style={styles.infoRow}>
                  <span style={styles.label}>Next billing</span>
                  <span style={styles.value}>{formatDate(profileData?.subscription_ends_at)}</span>
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '20px' }}>
              {subscriptionTier !== 'free' ? (
                <button className="pf-btn pf-btn-green" onClick={() => goToCustomerPortal()} style={{ padding: '10px 20px', fontSize: '14px' }}>
                  Manage Subscription
                </button>
              ) : (
                <button className="pf-btn pf-btn-blue" onClick={handleNavigateToPricing} style={{ padding: '10px 20px', fontSize: '14px' }}>
                  Upgrade Plan
                </button>
              )}
              <button className="pf-btn pf-btn-ghost" onClick={handleNavigateToPricing} style={{ padding: '10px 20px', fontSize: '14px' }}>
                View Pricing
              </button>
            </div>
          </div>

          {/* Right Column - Discord + Preferences stacked */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Discord Card */}
            <div className="pf-glass pf-fade pf-d3" style={{ padding: '24px' }}>
              <div style={cardHeader}>
                <div style={iconTile}>{Icons.discord('#8b9cb8', 17)}</div>
                <span style={cardTitle}>Discord</span>
              </div>
              {discordBanner}
            </div>

            {/* Preferences Card */}
            <div className="pf-glass pf-fade pf-d3" style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={cardHeader}>
                <div style={iconTile}>{Icons.settings('#8b9cb8', 17)}</div>
                <span style={cardTitle}>Preferences</span>
              </div>

              <div style={{ ...styles.label, marginBottom: '12px' }}>Odds Format</div>
              {oddsButtons(true)}
            </div>
          </div>
        </div>

        {/* Affiliate program */}
        <div className="pf-fade pf-d4" style={{ marginTop: '16px' }}>
          {affiliateCard}
        </div>

        {/* Sign Out Button */}
        <div className="pf-fade pf-d4" style={{ marginTop: '28px' }}>
          <button
            className="pf-btn pf-btn-ghost"
            onClick={() => signOut()}
            onMouseEnter={() => setHoveredButton('signout')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              borderColor: hoveredButton === 'signout' ? 'rgba(239, 68, 68, 0.55)' : undefined,
              color: hoveredButton === 'signout' ? '#ef4444' : '#94a3b8'
            }}
          >
            {Icons.logout(hoveredButton === 'signout' ? '#ef4444' : '#94a3b8')}
            Sign Out
          </button>
        </div>
      </div>
    </div>
  )
}
